import { Document, Page, StyleSheet, Text, View, Image } from '@react-pdf/renderer';
import type { PoModalModifiers, PurchaseOrderDetails, POModal, Vendor } from 'api/types';
import React from 'react';
import BridgeWaterLogo from 'assets/images/bridgewater-logo.png';
import LocationIcon from 'assets/images/distance.png';

interface PurchaseOrderPDFProps {
  data: POModal[];
  vendorBillingData: Vendor;
  deliveryAddress: string;
  poModifiers: PoModalModifiers;
  specialInstructions: string;
  poResponse: PurchaseOrderDetails;
  requester: string;
  poDate: string;
  purchaseDate: string;
  paymentTerms: string;
}

interface InfoBoxesProps {
  vendorBillingData?: {
    vendor_name?: string;
    billed_from_address1?: string;
    billed_from_city?: string;
    billed_from_state?: string;
    billed_from_postal_code?: string;
    billed_from_country?: string;
    phone_number?: string;
    payment_terms?: string;
  };
  requester: string;
  deliveryAddress: string;
  paymentTerms: string;
}

interface FooterProps {
  specialInstructions?: string;
  total: number;
  poModifiers: PoModalModifiers;
  finalTotal: number;
}

interface ContinuationHeaderProps {
  pageNumber: number;
  poNum: string;
}

const roundToTwoDecimalPlaces = (num: number | string): string => {
  if (typeof num === 'string') {
    num = parseFloat(num);
  }
  return (Math.round((num + Number.EPSILON) * 100) / 100).toFixed(2);
};

const formatDate = (date: Date): string =>
  date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });

const formatDateMDYYYY = (date: Date): string => {
  if (isNaN(date.getTime())) {
    console.log('Invalid date:', date);
    return 'Invalid Date';
  }

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  const result = `${month}/${day}/${year}`;
  return result;
};
// Helper function to estimate text height based on content length
const estimateTextHeight = (text: string, maxWidth: number, fontSize = 10): number => {
  const avgCharWidth = fontSize * 0.6; // Approximate character width
  const charsPerLine = Math.floor(maxWidth / avgCharWidth);
  const lines = Math.ceil(text.length / charsPerLine);
  const lineHeight = fontSize * 1.2; // Line height is typically 1.2x font size
  return lines * lineHeight;
};

const PDFHeader = ({
  poNum,
  poDate,
  purchaseDate
}: {
  poNum: string;
  poDate: string;
  purchaseDate: string;
}) => (
  <View
    style={{
      paddingBottom: 10,
      marginBottom: 10
    }}>
    <Text
      style={{
        fontSize: 20,
        fontWeight: 'bold',
        marginBottom: 10,
        marginHorizontal: 'auto'
      }}>
      Purchase Order
    </Text>
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
      {/* Logo and Company Name */}
      <View>
        <Image source={BridgeWaterLogo} style={{ width: '80%' }} />
        <Text style={{ fontSize: 18, marginTop: 5, color: '#4E4E4E' }}>
          Bridgewater Studio Inc.
        </Text>
      </View>
      {/* PO Details */}
      <View>
        <View style={{ marginBottom: 6, flexDirection: 'row' }}>
          <Image source={LocationIcon} style={{ width: 9 }} />
          <Text style={{ fontSize: 10, textAlign: 'center', marginLeft: 3 }}>
            4834 S Oakley Ave, Chicago, IL 60609 | (312) 702-1335
          </Text>
        </View>

        <View style={{ width: '70%', marginHorizontal: 'auto' }}>
          <View
            style={{
              flexDirection: 'row',
              marginBottom: 2,
              fontSize: 10
            }}>
            <View style={{ fontWeight: 'bold', width: '90px' }}>
              <Text>PO Number:</Text>
            </View>
            <View style={{ width: '90px' }}>
              <Text>{poNum}</Text>
            </View>
          </View>

          <View
            style={{
              flexDirection: 'row',
              fontSize: 10
            }}>
            <View style={{ fontWeight: 'bold', width: '90px' }}>
              <Text>PO Date:</Text>
            </View>
            <View style={{ width: '90px' }}>
              <Text>{poDate || formatDate(new Date())}</Text>
            </View>
          </View>

          <View
            style={{
              flexDirection: 'row',
              fontSize: 10
            }}>
            <View style={{ fontWeight: 'bold', width: '90px' }}>
              <Text>Purchase Date:</Text>
            </View>
            <View style={{ width: '90px' }}>
              <Text>{purchaseDate ? formatDateMDYYYY(new Date(purchaseDate)) : ''}</Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  </View>
);

const InfoBoxes: React.FC<InfoBoxesProps> = ({
  vendorBillingData,
  requester,
  deliveryAddress,
  paymentTerms
}) => (
  <View
    style={{
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 10,
      width: '100%'
    }}>
    {/* Vendor Details */}
    <View
      style={{
        width: '48%',
        backgroundColor: '#f4f4f4',
        borderRadius: 10,
        padding: 8
      }}>
      <Text style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>Vendor Details</Text>

      <View style={{ marginBottom: 10 }}>
        <Text style={{ fontWeight: 'bold', fontSize: 10 }}>Company Name:</Text>
        <Text style={{ fontSize: 10, marginRight: 1 }}>{vendorBillingData?.vendor_name || ''}</Text>
      </View>

      <View style={{ marginBottom: 10, flexDirection: 'row', flexWrap: 'wrap' }}>
        <Text style={{ fontWeight: 'bold', fontSize: 10 }}>Billing Address:</Text>
        <Text style={{ fontSize: 10, marginRight: 1 }}>
          {vendorBillingData
            ? [
                vendorBillingData.billed_from_address1,
                vendorBillingData.billed_from_city,
                vendorBillingData.billed_from_state,
                vendorBillingData.billed_from_postal_code,
                vendorBillingData.billed_from_country
              ]
                .filter(Boolean)
                .join(', ')
            : ''}
        </Text>
      </View>

      <View>
        <Text style={{ fontWeight: 'bold', fontSize: 10 }}>Phone:</Text>
        <Text style={{ fontSize: 10, marginRight: 1 }}>
          {vendorBillingData?.phone_number || ''}
        </Text>
      </View>
    </View>

    {/* Delivery Details */}
    <View
      style={{
        width: '48%',
        backgroundColor: '#f4f4f4',
        borderRadius: 10,
        padding: 8
      }}>
      <View style={{ marginBottom: 10 }}>
        <Text style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>Delivery Details</Text>
        <Text style={{ fontSize: 10 }}>
          {deliveryAddress || '4834 S Oakley Ave, Chicago, IL 60609'}
        </Text>
      </View>

      <View style={{ marginBottom: 10 }}>
        <Text style={{ fontWeight: 'bold', fontSize: 10, marginRight: 4 }}>Payment Terms:</Text>
        <Text style={{ fontSize: 10 }}>{paymentTerms}</Text>
      </View>

      <View>
        <Text style={{ fontWeight: 'bold', fontSize: 10, marginRight: 4 }}>Requested By:</Text>
        <Text style={{ fontSize: 10 }}>{requester || 'Requester Name'}</Text>
      </View>
    </View>
  </View>
);

const TableHeader = () => (
  <View style={styles.tableHeader} fixed>
    <Text style={[styles.tableCellHeading, styles.col1]}>Job</Text>
    <Text style={[styles.tableCellHeading, styles.col2]}>Item</Text>
    <Text style={[styles.tableCellHeading, styles.col3]}>Quantity</Text>
    <Text style={[styles.tableCellHeading, styles.col4]}>Unit Price</Text>
    <Text style={[styles.tableCellHeading, styles.col5]}>Total</Text>
  </View>
);

const TableRow = ({ item }: { item: POModal }) => {
  const price = parseFloat(item?.price ?? '0');
  const quantity = Number(item?.quantity ?? 0);
  const total = price * quantity;
  const description = item.description || '';

  return (
    <View
      style={[
        styles.tableRow,
        // Dynamic height based on content
        description.length > 100 ? { minHeight: 40 } : {}
      ]}>
      <Text style={[styles.tableCell, styles.col1]}>
        {item?.project_code && item?.project_name
          ? `${item.project_code} - ${item.project_name}`
          : item?.project?.project_code && item?.project?.project_name
          ? `${item.project.project_code} - ${item.project.project_name}`
          : ''}
      </Text>
      <Text style={[styles.tableCell, styles.col2, { flexWrap: 'wrap' }]}>{item.item_name}</Text>
      <Text style={[styles.tableCell, styles.col3]}>{quantity}</Text>
      <Text style={[styles.tableCell, styles.col4]}>${roundToTwoDecimalPlaces(price)}</Text>
      <Text style={[styles.tableCell, styles.col5]}>${roundToTwoDecimalPlaces(total)}</Text>
    </View>
  );
};

const Footer: React.FC<FooterProps> = ({ specialInstructions, total, poModifiers, finalTotal }) => (
  <View style={styles.footerRow}>
    <View style={styles.instructionsContainer}>
      <Text style={styles.instructionsTitle}>Special Instructions</Text>
      <View style={styles.instructionsBox}>
        <Text style={styles.instructionsText}>{specialInstructions}</Text>
      </View>

      <Text style={styles.instructionsTitle}>Authorized</Text>
      <View style={[styles.instructionsBox, { height: 30, marginBottom: 0 }]}>
        <Text style={styles.instructionsText}>{''}</Text>
      </View>
    </View>

    {/* Summary */}
    <View style={styles.summaryContainer}>
      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>Total Net:</Text>
        <Text style={styles.summaryValue}>${roundToTwoDecimalPlaces(total)}</Text>
      </View>

      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>Shipping:</Text>
        <Text style={styles.summaryValue}>
          ${roundToTwoDecimalPlaces(parseFloat(poModifiers.shipping_cost || '0'))}
        </Text>
      </View>

      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>Taxes:</Text>
        <Text style={styles.summaryValue}>
          ${roundToTwoDecimalPlaces(parseFloat(poModifiers.estimated_taxes || '0'))}
        </Text>
      </View>

      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>Discount:</Text>
        <Text style={styles.summaryValue}>
          ${roundToTwoDecimalPlaces(parseFloat(poModifiers.discount || '0'))}
        </Text>
      </View>

      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>Deposit:</Text>
        <Text style={styles.summaryValue}>
          ${roundToTwoDecimalPlaces(parseFloat(poModifiers.deposit || '0'))}
        </Text>
      </View>

      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>Refund:</Text>
        <Text style={styles.summaryValue}>
          ${roundToTwoDecimalPlaces(parseFloat(poModifiers.refund || '0'))}
        </Text>
      </View>

      <View style={styles.totalRow}>
        <Text style={styles.totalLabel}>TOTAL:</Text>
        <Text style={styles.totalValue}>${roundToTwoDecimalPlaces(finalTotal)}</Text>
      </View>
    </View>
  </View>
);

const ContinuationHeader: React.FC<ContinuationHeaderProps> = ({ pageNumber, poNum }) => (
  <View style={styles.continuationHeader}>
    <Text style={styles.continuationText}>
      Purchase Order #{poNum} - Continued (Page {pageNumber})
    </Text>
  </View>
);

const PageNumber = ({ currentPage, totalPages }: { currentPage: number; totalPages: number }) => (
  <View style={styles.pageNumber}>
    <Text style={styles.pageNumberText}>
      Page {currentPage} of {totalPages}
    </Text>
  </View>
);

const PurchaseOrderPdfView: React.FC<PurchaseOrderPDFProps> = ({
  data,
  vendorBillingData,
  deliveryAddress,
  poModifiers,
  specialInstructions,
  poResponse,
  requester,
  poDate,
  purchaseDate,
  paymentTerms
}) => {
  const itemsData = data || [];

  let total = 0;
  itemsData.forEach((item) => {
    const price = parseFloat(item.price ?? '0');
    const quantity =
      typeof item.quantity === 'string' ? parseFloat(item.quantity) : item.quantity ?? 0;
    total += price * quantity;
  });

  const parseFloatSafe = (value: string | undefined): number => {
    if (!value) return 0;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  const finalTotal =
    total +
    parseFloatSafe(poModifiers.shipping_cost) +
    parseFloatSafe(poModifiers.estimated_taxes) -
    parseFloatSafe(poModifiers.discount) -
    parseFloatSafe(poModifiers.deposit) -
    parseFloatSafe(poModifiers.refund);

  // Improved pagination constants
  const PAGE_HEIGHT = 842;
  const FOOTER_HEIGHT = 200;
  const HEADER_HEIGHT = 180; // Header + info boxes
  const TABLE_HEADER_HEIGHT = 30;
  const CONTINUATION_HEADER_HEIGHT = 25;

  const FIRST_PAGE_AVAILABLE_HEIGHT =
    PAGE_HEIGHT - FOOTER_HEIGHT - HEADER_HEIGHT - TABLE_HEADER_HEIGHT;
  const CONTINUATION_PAGE_AVAILABLE_HEIGHT =
    PAGE_HEIGHT - FOOTER_HEIGHT - CONTINUATION_HEADER_HEIGHT - TABLE_HEADER_HEIGHT;

  const ROW_HEIGHT = 24;
  const maxRowsFirstPage = Math.floor(FIRST_PAGE_AVAILABLE_HEIGHT / ROW_HEIGHT);
  const maxRowsContinuationPage = Math.floor(CONTINUATION_PAGE_AVAILABLE_HEIGHT / ROW_HEIGHT);

  // Chunk items into pages with better logic
  const getItemPages = (): POModal[][] => {
    if (itemsData.length === 0) return [[]];

    const pages: POModal[][] = [];
    let currentPage: POModal[] = [];
    let currentPageHeight = 0;
    let isFirstPage = true;

    const maxRowsForCurrentPage = () => (isFirstPage ? maxRowsFirstPage : maxRowsContinuationPage);
    const availableHeightForCurrentPage = () =>
      isFirstPage ? FIRST_PAGE_AVAILABLE_HEIGHT : CONTINUATION_PAGE_AVAILABLE_HEIGHT;

    itemsData.forEach((item) => {
      const description = item.description || '';
      const estimatedRowHeight = Math.max(
        ROW_HEIGHT,
        Math.min(60, estimateTextHeight(description, 200, 10) + 8)
      );

      // Check if we need a new page
      const needsNewPage =
        currentPage.length >= maxRowsForCurrentPage() ||
        currentPageHeight + estimatedRowHeight > availableHeightForCurrentPage();

      if (needsNewPage && currentPage.length > 0) {
        pages.push([...currentPage]);
        currentPage = [];
        currentPageHeight = 0;
        isFirstPage = false;
      }

      currentPage.push(item);
      currentPageHeight += estimatedRowHeight;
    });

    if (currentPage.length > 0) {
      pages.push(currentPage);
    }

    return pages;
  };

  const itemPages = getItemPages();
  const totalPages = itemPages.length;
  const isLastPage = (pageIndex: number) => pageIndex === itemPages.length - 1;

  return (
    <Document>
      {itemPages.map((pageItems, pageIndex) => (
        <Page key={pageIndex} size="A4" style={styles.page} wrap>
          {/* First Page Header */}
          {pageIndex === 0 && (
            <>
              <PDFHeader
                poNum={poResponse.purchase_order_number || '0000'}
                poDate={poDate}
                purchaseDate={purchaseDate}
              />
              <InfoBoxes
                vendorBillingData={vendorBillingData}
                requester={requester}
                deliveryAddress={deliveryAddress}
                paymentTerms={paymentTerms}
              />
            </>
          )}

          {/* Continuation Header for subsequent pages */}
          {pageIndex > 0 && (
            <ContinuationHeader
              pageNumber={pageIndex + 1}
              poNum={poResponse.purchase_order_number || '0000'}
            />
          )}

          {/* Table */}
          <View style={styles.tableContainer}>
            <TableHeader />
            {pageItems.map((item, index) => (
              <TableRow key={`${pageIndex}-${index}`} item={item} />
            ))}
          </View>

          {/* Footer only on last page */}
          {isLastPage(pageIndex) && (
            <View wrap={false}>
              <Footer
                specialInstructions={specialInstructions}
                total={total}
                poModifiers={poModifiers}
                finalTotal={finalTotal}
              />
            </View>
          )}

          <PageNumber currentPage={pageIndex + 1} totalPages={totalPages} />
        </Page>
      ))}
    </Document>
  );
};

export default PurchaseOrderPdfView;

const styles = StyleSheet.create({
  page: {
    paddingVertical: 10,
    paddingHorizontal: 30,
    fontSize: 10
  },
  headerContainer: {
    flexDirection: 'column',
    marginBottom: 10
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 20
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20
  },
  companyName: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#4E4E4E',
    marginLeft: 5
  },
  headerRight: {
    width: '50%',
    flexDirection: 'column',
    alignItems: 'flex-end'
  },
  addressText: {
    fontSize: 8,
    marginBottom: 10,
    textAlign: 'right'
  },
  poDetailsRow: {
    flexDirection: 'row',
    marginBottom: 3
  },
  poLabel: {
    fontSize: 8,
    fontWeight: 'bold',
    width: 80,
    textAlign: 'left'
  },
  poValue: {
    fontSize: 8,
    width: 100,
    textAlign: 'left'
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 15
  },
  infoBox: {
    width: '48%',
    backgroundColor: '#f4f4f4',
    borderRadius: 5,
    padding: 10
  },
  infoTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 6
  },
  infoItem: {
    flexDirection: 'row',
    marginBottom: 4
  },
  infoLabel: {
    fontSize: 8,
    fontWeight: 'bold',
    marginRight: 4
  },
  infoValue: {
    fontSize: 8
  },
  tableContainer: {
    marginTop: 15,
    marginBottom: 15
  },
  tableHeader: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    backgroundColor: '#335d87',
    paddingVertical: 6,
    paddingHorizontal: 4,
    color: '#fff',
    alignItems: 'center'
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#dddddd',
    paddingVertical: 4,
    paddingHorizontal: 4,
    minHeight: 24,
    alignItems: 'flex-start'
  },
  tableCell: {
    fontSize: 10,
    padding: 2
  },
  tableCellHeading: {
    fontSize: 10,
    fontWeight: 'bold'
  },
  col1: { width: '25%', textAlign: 'left' },
  col2: { width: '35%', textAlign: 'left' },
  col3: { width: '10%', textAlign: 'center' },
  col4: { width: '15%', textAlign: 'center' },
  col5: { width: '15%', textAlign: 'center' },
  footerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 15
  },
  instructionsContainer: {
    width: '60%'
  },
  instructionsTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    marginBottom: 3
  },
  instructionsBox: {
    borderWidth: 1,
    borderColor: '#ddd',
    padding: 8,
    height: 65,
    marginBottom: 10
  },
  summaryContainer: {
    width: '35%',
    backgroundColor: '#f4f4f4',
    padding: 8,
    borderRadius: 5,
    height: '80%'
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 3
  },
  summaryLabel: {
    fontSize: 10,
    paddingBottom: 4,
    textAlign: 'left'
  },
  summaryValue: {
    fontSize: 10,
    paddingBottom: 4,
    textAlign: 'right'
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
    paddingTop: 4,
    borderTopColor: '#ddd'
  },
  totalLabel: {
    fontSize: 10,
    paddingVertical: 0,
    fontWeight: 'bold'
  },
  totalValue: {
    fontSize: 10,
    paddingVertical: 0,
    fontWeight: 'bold'
  },
  instructionsText: {
    fontSize: 8
  },
  continuationHeader: {
    marginBottom: 10,
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#dddddd'
  },
  continuationText: {
    fontSize: 10,
    fontWeight: 'bold',
    textAlign: 'center'
  },
  pageNumber: {
    position: 'absolute',
    bottom: 10,
    left: 0,
    right: 0
  },
  pageNumberText: {
    fontSize: 8,
    color: '#666',
    textAlign: 'right',
    paddingRight: 5
  }
});
